import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type {
  NursingAcuityDailyRow,
  NursingAcuityItemRow,
  NursingAcuityRequirementRow,
} from "@shared/api";

const fmtPct = (v: number | null) => v == null ? "—" : `${v.toFixed(1)}%`;
const fmtPt = (v: number | null) => v == null ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}pt`;
const fmtN = (v: number | null) => v == null ? "—" : Math.round(v).toLocaleString("ja-JP");
const fmtDate = (v: string) => {
  const d = new Date(`${v}T00:00:00`);
  return Number.isNaN(d.getTime()) ? v : `${d.getMonth() + 1}/${d.getDate()}`;
};

const STATUS_TONE: Record<string, string> = {
  充足: "border-emerald-200 bg-emerald-50 text-emerald-800",
  注意: "border-amber-200 bg-amber-50 text-amber-800",
  危険: "border-orange-200 bg-orange-50 text-orange-800",
  未達: "border-red-200 bg-red-50 text-red-800",
  データなし: "border-rose-300 bg-rose-50 text-rose-800",
  測定確認: "border-amber-200 bg-amber-50 text-amber-800",
  OK: "border-emerald-200 bg-emerald-50 text-emerald-800",
  要確認: "border-rose-300 bg-rose-50 text-rose-800",
  平常: "border-emerald-200 bg-emerald-50 text-emerald-800",
  重点確認: "border-rose-300 bg-rose-50 text-rose-800",
};

function tone(status: string): string {
  return STATUS_TONE[status] ?? "border-slate-200 bg-slate-50 text-slate-700";
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${tone(status)}`}>
      {status}
    </span>
  );
}

function MarginBar({ row }: { row: NursingAcuityRequirementRow }) {
  if (row.value_pct == null || row.threshold_pct == null) {
    return <div className="h-2 rounded bg-slate-100" />;
  }
  const max = Math.max(row.value_pct, row.threshold_pct, 1) * 1.18;
  const valueX = Math.min(100, Math.max(0, row.value_pct / max * 100));
  const thresholdX = Math.min(100, Math.max(0, row.threshold_pct / max * 100));
  return (
    <div className="relative h-2 rounded bg-slate-100 overflow-visible" title={`基準 ${fmtPct(row.threshold_pct)}`}>
      <div
        className={`h-2 rounded ${row.margin_pt != null && row.margin_pt >= 0 ? "bg-emerald-500" : "bg-red-500"}`}
        style={{ width: `${valueX}%` }}
      />
      <span
        className="absolute -top-1 h-4 w-0.5 bg-slate-900"
        style={{ left: `${thresholdX}%` }}
      />
    </div>
  );
}

function Sparkline({ rows }: { rows: NursingAcuityDailyRow[] }) {
  const points = rows.filter((r) => r.value_pct != null);
  if (points.length < 2) {
    return <div className="h-14 flex items-center text-xs text-slate-400">日次推移なし</div>;
  }
  const values = points.map((r) => Number(r.value_pct));
  const threshold = points.find((r) => r.threshold_pct != null)?.threshold_pct ?? null;
  const domain = threshold == null ? values : [...values, threshold];
  const min = Math.max(0, Math.min(...domain) - 5);
  const max = Math.max(...domain) + 5;
  const x = (i: number) => 3 + i / (points.length - 1) * 214;
  const y = (v: number) => 50 - (v - min) / Math.max(1, max - min) * 44;
  const path = points.map((r, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(Number(r.value_pct)).toFixed(1)}`).join(" ");
  const thresholdY = threshold == null ? null : y(threshold);
  return (
    <div>
      <svg viewBox="0 0 220 56" className="w-full h-14" role="img" aria-label="月初来の日次推移">
        {thresholdY != null && (
          <line x1="2" x2="218" y1={thresholdY} y2={thresholdY} stroke="#475569" strokeDasharray="4 3" strokeWidth="1" />
        )}
        <path d={path} fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx={x(points.length - 1)} cy={y(values[values.length - 1])} r="3" fill="#059669" />
      </svg>
      <div className="flex justify-between text-[10px] text-slate-400 -mt-1">
        <span>{fmtDate(points[0].date)}</span>
        <span>{fmtDate(points[points.length - 1].date)}</span>
      </div>
    </div>
  );
}

function RequirementCard({
  row,
  daily,
}: {
  row: NursingAcuityRequirementRow;
  daily: NursingAcuityDailyRow[];
}) {
  const noData = row.denominator === 0 || row.value_pct == null;
  return (
    <article className={`rounded-xl border p-3 bg-white shadow-sm ${noData ? "border-rose-200" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-slate-500">{row.category}</div>
          <h3 className="mt-0.5 text-sm font-semibold text-slate-900 leading-snug">{row.criterion}</h3>
        </div>
        <StatusBadge status={row.status} />
      </div>

      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <div className={`text-3xl font-bold tabular-nums ${row.margin_pt != null && row.margin_pt < 0 ? "text-red-700" : "text-slate-900"}`}>
            {fmtPct(row.value_pct)}
          </div>
          {row.coefficient_pct > 0 && row.raw_pct != null && (
            <div className="text-[11px] text-slate-500">
              実測 {fmtPct(row.raw_pct)} ＋ 救急搬送係数 {row.coefficient_pct.toFixed(0)}pt
            </div>
          )}
        </div>
        <div className="text-right text-xs">
          <div className="text-slate-500">必要基準</div>
          <div className="font-semibold tabular-nums">{fmtPct(row.threshold_pct)}</div>
        </div>
      </div>

      <div className="mt-2">
        <MarginBar row={row} />
        <div className="mt-1 flex justify-between text-[11px]">
          <span className={row.margin_pt != null && row.margin_pt < 0 ? "font-semibold text-red-700" : "font-semibold text-emerald-700"}>
            余裕 {fmtPt(row.margin_pt)}
          </span>
          <span className="text-slate-500">{fmtN(row.numerator)} / {fmtN(row.denominator)} 患者日</span>
        </div>
      </div>

      <div className="mt-2 border-t border-slate-100 pt-1">
        <Sparkline rows={daily} />
      </div>
      <p className="mt-2 text-[11px] leading-relaxed text-slate-500">{row.note}</p>
    </article>
  );
}

function ItemTable({ rows }: { rows: NursingAcuityItemRow[] }) {
  if (rows.length === 0) return <div className="p-4 text-sm text-slate-500">項目別データはありません。</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="px-3 py-2 text-left">項目</th>
            <th className="px-3 py-2 text-left">区分</th>
            <th className="px-3 py-2 text-right">該当患者日</th>
            <th className="px-3 py-2 text-right">割合</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={`${r.category}-${r.ticket}-${r.item_name}-${i}`} className="hover:bg-slate-50">
              <td className="px-3 py-2">
                <div className="font-medium text-slate-800">{r.item_name}</div>
                <div className="text-[10px] text-slate-400">{r.major}・{r.score_unit}・{r.points}点</div>
              </td>
              <td className="px-3 py-2">
                {r.high_intensity === 1
                  ? <span className="rounded bg-violet-100 px-1.5 py-0.5 text-violet-700">高強度A</span>
                  : <span className="text-slate-500">{r.ticket}</span>}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">{fmtN(r.numerator)}</td>
              <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtPct(r.share_pct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function NursingAcuityPanel() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["nursing-acuity"],
    queryFn: api.nursingAcuity,
    staleTime: 5 * 60 * 1000,
  });
  const [itemCategory, setItemCategory] = useState("一般371床");

  const dailyByRequirement = useMemo(() => {
    const grouped = new Map<string, NursingAcuityDailyRow[]>();
    for (const row of data?.daily ?? []) {
      const list = grouped.get(row.requirement_id) ?? [];
      list.push(row);
      grouped.set(row.requirement_id, list);
    }
    return grouped;
  }, [data?.daily]);

  const itemCategories = useMemo(
    () => Array.from(new Set((data?.items ?? []).map((r) => r.category))),
    [data?.items],
  );
  const effectiveItemCategory = itemCategories.includes(itemCategory)
    ? itemCategory
    : (itemCategories[0] ?? "");
  const selectedItems = (data?.items ?? []).filter((r) => r.category === effectiveItemCategory);

  if (isLoading) {
    return <div className="card p-8 animate-pulse text-slate-400">看護必要度速報を読み込み中…</div>;
  }
  if (error) {
    return (
      <div className="card border-red-200 bg-red-50 p-6">
        <h2 className="font-semibold text-red-800">看護必要度速報を取得できません</h2>
        <p className="mt-1 text-sm text-red-700">{(error as Error).message}</p>
      </div>
    );
  }
  if (!data?.available || !data.meta) {
    return (
      <div className="card p-8">
        <h2 className="text-lg font-semibold text-slate-800">看護必要度速報は準備中です</h2>
        <p className="mt-2 text-sm text-slate-600">日次の集計テーブルがまだ配信DBへ投入されていません。</p>
      </div>
    );
  }

  const meta = data.meta;
  const okCount = data.requirements.filter((r) => r.status === "充足").length;
  const warningCount = data.requirements.filter((r) => ["注意", "危険", "未達"].includes(r.status)).length;
  const missingCount = data.requirements.filter((r) => r.status === "データなし").length;
  const groups = Array.from(new Set(data.requirements.map((r) => r.category)));

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-white p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-900">看護必要度速報</h2>
              <span className="rounded-full bg-emerald-700 px-2 py-0.5 text-xs font-semibold text-white">beta</span>
            </div>
            <p className="mt-1 text-sm text-slate-600">
              {fmtDate(meta.month_start)}〜{fmtDate(meta.cutoff_date)}の月初来速報
              <span className="mx-2 text-slate-300">|</span>
              一般 {meta.general_beds}床・救急車 年間{meta.ambulance_annual.toLocaleString("ja-JP")}台
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm">
              <span className="text-slate-500">データ上限 </span>
              <strong>{meta.cutoff_date}</strong>
            </span>
            <button
              onClick={() => void refetch()}
              disabled={isFetching}
              className="no-print rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
            >
              {isFetching ? "更新中…" : "再読込"}
            </button>
          </div>
        </div>

        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="text-xs text-slate-500">ウォッチ要件</div>
            <div className="mt-1 text-2xl font-bold">{data.requirements.length}<span className="ml-1 text-sm font-normal">項目</span></div>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-white p-3">
            <div className="text-xs text-emerald-700">充足</div>
            <div className="mt-1 text-2xl font-bold text-emerald-700">{okCount}<span className="ml-1 text-sm font-normal">項目</span></div>
          </div>
          <div className="rounded-lg border border-amber-200 bg-white p-3">
            <div className="text-xs text-amber-700">注意・危険・未達</div>
            <div className="mt-1 text-2xl font-bold text-amber-700">{warningCount}<span className="ml-1 text-sm font-normal">項目</span></div>
          </div>
          <div className="rounded-lg border border-rose-200 bg-white p-3">
            <div className="text-xs text-rose-700">データ経路 要確認</div>
            <div className="mt-1 text-2xl font-bold text-rose-700">{missingCount}<span className="ml-1 text-sm font-normal">項目</span></div>
          </div>
        </div>

        <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs leading-relaxed text-sky-900">
          <strong>会計反映待ちを2日確保：</strong>
          1日分は3日の日を跨いだ後の朝に集計します。B評価票・一般A/C会計・ICU/HCU会計が
          すべてD−2まで揃わなければ公開用データを更新しません。
        </div>
      </section>

      <section>
        <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">施設基準 全要件</h2>
            <p className="text-xs text-slate-500">黒線が必要基準。数値はD−2までの月初来累計です。</p>
          </div>
          <div className="text-xs text-slate-500">生成 {meta.generated_at}</div>
        </div>

        <div className="space-y-4">
          {groups.map((group) => (
            <div key={group}>
              <h3 className="mb-2 text-sm font-semibold text-slate-700">{group}</h3>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {data.requirements.filter((r) => r.category === group).map((row) => (
                  <RequirementCard
                    key={row.requirement_id}
                    row={row}
                    daily={dailyByRequirement.get(row.requirement_id) ?? []}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="card overflow-hidden">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="font-semibold text-slate-900">一般病棟別の差</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            病棟の合否判定ではなく、病院全体の実測値との差を見る早期警戒です。救急搬送係数10%は病棟へ配賦していません。
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left">病棟</th>
                <th className="px-3 py-2 text-right">患者日</th>
                <th className="px-3 py-2 text-right">基準① 実測</th>
                <th className="px-3 py-2 text-right">全体差</th>
                <th className="px-3 py-2 text-right">基準② 実測</th>
                <th className="px-3 py-2 text-right">全体差</th>
                <th className="px-3 py-2 text-center">シグナル</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.wards.map((r) => (
                <tr key={r.ward_cd} className={r.signal === "重点確認" ? "bg-rose-50/60" : r.signal === "注意" ? "bg-amber-50/50" : ""}>
                  <td className="px-3 py-2 font-medium">{r.ward}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtN(r.denominator)}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtPct(r.criterion1_pct)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${r.diff1_pt != null && r.diff1_pt < 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtPt(r.diff1_pt)}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">{fmtPct(r.criterion2_pct)}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${r.diff2_pt != null && r.diff2_pt < 0 ? "text-red-700" : "text-emerald-700"}`}>{fmtPt(r.diff2_pt)}</td>
                  <td className="px-3 py-2 text-center"><StatusBadge status={r.signal} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card overflow-hidden">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="font-semibold text-slate-900">該当を作っている評価項目</h2>
          <p className="mt-0.5 text-xs text-slate-500">A/B/Cの項目別該当患者日。割合の大きい順に表示します。</p>
          <div className="mt-2 flex flex-wrap gap-1">
            {itemCategories.map((category) => (
              <button
                key={category}
                onClick={() => setItemCategory(category)}
                className={`rounded-full border px-3 py-1 text-xs ${
                  category === effectiveItemCategory
                    ? "border-emerald-600 bg-emerald-600 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
        <ItemTable rows={selectedItems} />
      </section>

      <section className="card overflow-hidden">
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 className="font-semibold text-slate-900">データ品質・判定境界</h2>
          <p className="mt-0.5 text-xs text-slate-500">速報を正式値と取り違えないための自動チェックです。</p>
        </div>
        <div className="grid gap-px bg-slate-200 sm:grid-cols-2 xl:grid-cols-3">
          {data.quality.map((q) => (
            <div key={q.check_id} className="bg-white p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-medium text-slate-800">{q.label}</div>
                <StatusBadge status={q.status} />
              </div>
              <div className="mt-1 text-lg font-semibold text-slate-900">{q.value}</div>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">{q.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
        <h2 className="font-semibold">読み方の注意</h2>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs leading-relaxed">
          <li>このbetaは月初来の早期警戒です。正式な施設基準判定は直近3か月かつ除外患者調整後に行います。</li>
          <li>一般病棟の救急搬送係数10%は、年間救急車10,000台・一般{meta.general_beds}床の病院全体に適用しています。病棟別には配賦しません。</li>
          <li>回復期40床は新規入院を受けず、新規入院は一般430床で受ける運用です。病棟別値は届出判定ではなく運用確認に使います。</li>
          <li>救命救急1・2は会計上の経路が0件です。EICU等の物理配置を代理値にせず、届出・会計・評価票の対応確認が必要です。</li>
        </ul>
      </section>
    </div>
  );
}
